import { Clock, MailCheck, XCircle } from "lucide-react";

import { Logo } from "@/components/common/logo";
import { MotionPage } from "@/components/common/motion";
import { DoctorStatusBadge } from "@/components/common/status-badge";
import { ShellSignOutButton } from "@/components/layout/app-shell";
import { Button } from "@/components/ui/button";
import { Link } from "react-router";
import { formatDate } from "@/lib/format";
import type { Doctor } from "@/types/doctor";

/** Shown to clinicians whose registration is awaiting or failed review. */
export function PendingReview({ doctor }: { doctor: Doctor }) {
  const rejected = doctor.status === "rejected";
  const Icon = rejected ? XCircle : Clock;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="aurora-soft" aria-hidden="true" />

      <header className="relative z-10 mx-auto flex w-full max-w-3xl items-center justify-between px-5 py-6">
        <Logo />
        <ShellSignOutButton />
      </header>

      <MotionPage as="main" className="relative z-10 mx-auto w-full max-w-3xl px-5 pb-20">
        <div className="card-surface p-8 sm:p-10">
          <div className="flex flex-wrap items-center gap-4">
            <span
              className={`grid size-12 place-items-center rounded-2xl ${
                rejected ? "bg-destructive/10 text-destructive" : "bg-warning/12 text-warning"
              }`}
            >
              <Icon className="size-6" />
            </span>
            <DoctorStatusBadge status={doctor.status} />
          </div>

          <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-balance">
            {rejected ? "Your registration wasn't approved" : "Your registration is under review"}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {rejected
              ? "The HealNova verification team couldn't approve this profile. Update your details and our team will take another look, or contact support if you think this is a mistake."
              : "Thanks for registering, " +
                doctor.name +
                ". Our verification team is checking your credentials. You'll get access to the full portal as soon as your profile is approved."}
          </p>

          <dl className="mt-8 grid gap-5 border-t border-border pt-8 sm:grid-cols-3">
            {[
              ["Specialization", doctor.specialization],
              ["Experience", `${doctor.experience} years`],
              ["Submitted", formatDate(doctor.createdAt)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  {label}
                </dt>
                <dd className="mt-1.5 text-sm font-medium">{value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/profile">Review my details</Link>
            </Button>
            <Button asChild variant="outline">
              <a href="mailto:support@healnova.ai">
                <MailCheck className="size-4" /> Contact support
              </a>
            </Button>
          </div>
        </div>
      </MotionPage>
    </div>
  );
}
